import { ChainId, Token } from '@uniswap/sdk'

// ============================================
// CHAIN IDs
// ============================================

// Custom chain IDs (not in @uniswap/sdk ChainId enum)
export const CustomChainId = {
  CONFLUX_ESPACE_TESTNET: 71,
  CONFLUX_ESPACE_MAINNET: 1030
} as const

// ============================================
// CHAIN CONFIGURATION INTERFACE
// ============================================

export interface ChainConfig {
  chainId: number
  name: string
  shortName: string
  nativeCurrency: {
    name: string
    symbol: string
    decimals: number
  }
  rpcUrl: string
  blockExplorerUrl?: string
  // Contract addresses
  contracts: {
    factory: string
    router: string
    weth: string // Wrapped native token
    multicall?: string
  }
  // Feature flags
  features: {
    hasMulticall: boolean
    hasENS: boolean
  }
  // Remote config URLs
  tokenListUrl?: string
  lpListUrl?: string
}

// ============================================
// CHAIN CONFIGURATIONS
// ============================================

export const CHAIN_CONFIGS: { [chainId: number]: ChainConfig } = {
  // Conflux eSpace Mainnet
  [CustomChainId.CONFLUX_ESPACE_MAINNET]: {
    chainId: CustomChainId.CONFLUX_ESPACE_MAINNET,

    name: 'Conflux eSpace',
    shortName: 'Conflux',
    nativeCurrency: {
      name: 'Conflux',
      symbol: 'CFX',
      decimals: 18
    },
    rpcUrl: 'https://evm.confluxrpc.com',
    blockExplorerUrl: 'https://evm.confluxscan.net',
    contracts: {
      factory: '0x6f350F0A94De78F3F20153DFf8c87E54F648aCE8',
      router: '0x64e5F66596ccF6A679f871DFE7E9674B45DC6715',
      weth: '0x491347f20dfbBcdFeFB3A8F9cb4346d0B5495AEa'
    },
    features: {
      hasMulticall: false,
      hasENS: false
    },
    tokenListUrl: 'https://sc.minax.io/dex/mainnet/conf/tokenlist.json',
    lpListUrl: 'https://sc.minax.io/dex/mainnet/conf/lplist.json'
  },

  // Conflux eSpace Testnet
  [CustomChainId.CONFLUX_ESPACE_TESTNET]: {
    chainId: CustomChainId.CONFLUX_ESPACE_TESTNET,
    name: 'Conflux eSpace Testnet',
    shortName: 'CFX Testnet',
    nativeCurrency: {
      name: 'Conflux',
      symbol: 'CFX',
      decimals: 18
    },
    rpcUrl: 'https://evmtestnet.confluxrpc.com',
    blockExplorerUrl: 'https://evmtestnet.confluxscan.io',
    contracts: {
      factory: '0x51BB856860Bcbc4c18711054E92aa0d4Cc42A810',
      router: '0x59c80cB42917f997Cf99154B56aF9B663E091e73',
      weth: '0xE7185944C589A39f44Ffc31dF7Bd2edcB497e268'
    },
    features: {
      hasMulticall: false,
      hasENS: false
    },
    tokenListUrl: 'https://sc.minax.io/dex/testnet/conf/tokenlist.json',
    lpListUrl: 'https://sc.minax.io/dex/testnet/conf/lplist.json'
  }
}

// ============================================
// ACTIVE CHAIN SELECTION
// ============================================

// Active chain ID - change this to switch networks
export const ACTIVE_CHAIN_ID = CustomChainId.CONFLUX_ESPACE_MAINNET

// Get active chain config
export function getActiveChainConfig(): ChainConfig {
  const config = CHAIN_CONFIGS[ACTIVE_CHAIN_ID]
  if (!config) {
    throw new Error(`No configuration found for chain ID: ${ACTIVE_CHAIN_ID}`)
  }
  return config
}

// Get chain config by ID
export function getChainConfig(chainId: number): ChainConfig | undefined {
  return CHAIN_CONFIGS[chainId]
}

// ============================================
// HELPER FUNCTIONS
// ============================================

// Get WETH token for a chain
export function getWethToken(chainId: number): Token | undefined {
  const config = CHAIN_CONFIGS[chainId]
  if (!config) return undefined

  return new Token(
    chainId as ChainId,
    config.contracts.weth,
    18,
    'W' + config.nativeCurrency.symbol,
    'Wrapped ' + config.nativeCurrency.name
  )
}

// Get native currency symbol
export function getNativeCurrencySymbol(chainId?: number): string {
  if (!chainId) return 'ETH'
  return CHAIN_CONFIGS[chainId]?.nativeCurrency.symbol ?? 'ETH'
}

// Get router address
export function getRouterAddress(chainId?: number): string {
  const id = chainId ?? ACTIVE_CHAIN_ID
  const config = CHAIN_CONFIGS[id]
  if (!config) {
    throw new Error(`No router address for chain ID: ${id}`)
  }
  return config.contracts.router
}

// Get factory address
export function getFactoryAddress(chainId?: number): string {
  const id = chainId ?? ACTIVE_CHAIN_ID
  const config = CHAIN_CONFIGS[id]
  if (!config) {
    throw new Error(`No factory address for chain ID: ${id}`)
  }
  return config.contracts.factory
}

// Check if chain has multicall
export function hasMulticall(chainId?: number): boolean {
  if (!chainId) return false
  return CHAIN_CONFIGS[chainId]?.features.hasMulticall ?? false
}

// Check if chain has ENS
export function hasENS(chainId?: number): boolean {
  if (!chainId) return false
  return CHAIN_CONFIGS[chainId]?.features.hasENS ?? false
}

// Get all supported chain IDs
export function getSupportedChainIds(): number[] {
  return Object.keys(CHAIN_CONFIGS).map(Number)
}
